import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star } from 'lucide-react';
import { calculateLevel, MAX_LEVEL, LEVEL_REQUIREMENTS } from '@/utils/todoHelpers';

interface LevelTrackerProps {
  totalPoints: number;
  onLevelUp?: (newLevel: number) => void;
  onResetAnimation?: () => void;
  isInitialAppearance?: boolean;
  isMobile?: boolean;
  showMissions?: boolean;
}
export default function LevelTracker({
  totalPoints,
  onLevelUp,
  onResetAnimation,
  isInitialAppearance = false,
  isMobile = false,
  showMissions = false
}: LevelTrackerProps) {
  const [isLevelingUp, setIsLevelingUp] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [animatedLevel, setAnimatedLevel] = useState(1);
  const [animatedPoints, setAnimatedPoints] = useState(0);
  const [animatedProgress, setAnimatedProgress] = useState(0);
  const previousLevel = useRef<number | null>(null);
  const isInitialMount = useRef(true);
  const {
    level,
    pointsToNext,
    progressPercent
  } = calculateLevel(totalPoints);

  // Calculate current points within the level
  const levelStart = level > 1 ? LEVEL_REQUIREMENTS[level - 1] : 0;
  const currentLevelPoints = totalPoints - levelStart;
  const totalLevelPoints = currentLevelPoints + pointsToNext;

  // Reset animation trigger
  useEffect(() => {
    if (onResetAnimation && totalPoints === 0 && level === 1 && !isInitialMount.current && !isResetting) {
      const currentLevel = animatedLevel || level;
      const currentPoints = animatedPoints || totalPoints;
      const currentProgress = animatedProgress || progressPercent;
      
      if (currentLevel > 1 || currentPoints > 0 || currentProgress > 0) {
        setIsResetting(true);
        
        // Animate countdown
        const startTime = Date.now();
        const duration = 2500; // 2.5 seconds
        
        const animate = () => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min(elapsed / duration, 1);
          const easeOut = 1 - Math.pow(1 - progress, 3);
          
          setAnimatedLevel(Math.ceil(currentLevel - (currentLevel - 1) * easeOut));
          setAnimatedPoints(Math.ceil(currentPoints - currentPoints * easeOut));
          setAnimatedProgress(currentProgress - currentProgress * easeOut);
          
          if (progress < 1) {
            requestAnimationFrame(animate);
          } else {
            setIsResetting(false);
            setAnimatedLevel(1);
            setAnimatedPoints(0);
            setAnimatedProgress(0);
            onResetAnimation();
          }
        };
        
        requestAnimationFrame(animate);
      }
    }
  }, [totalPoints, level, onResetAnimation]);

  // Level up detection
  useEffect(() => {
    // Skip animation on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      previousLevel.current = level;
      setAnimatedLevel(level);
      setAnimatedPoints(totalPoints);
      setAnimatedProgress(progressPercent);
      return;
    }

    // Initialize previousLevel on first render after initial mount
    if (previousLevel.current === null) {
      previousLevel.current = level;
      return;
    }

    // Update animated values when not resetting
    if (!isResetting) {
      setAnimatedLevel(level);
      setAnimatedPoints(totalPoints);
      setAnimatedProgress(progressPercent);
    }

    // Only trigger animation if level actually increased
    if (level > previousLevel.current) {
      setIsLevelingUp(true);
      onLevelUp?.(level);
      const timer = setTimeout(() => {
        setIsLevelingUp(false);
        previousLevel.current = level; // Update previousLevel after animation completes
      }, 1000); // Exactly 1 second

      return () => clearTimeout(timer);
    }

    // Update previousLevel if level decreased (shouldn't happen but safety check)
    if (level < previousLevel.current) {
      previousLevel.current = level;
    }
  }, [level, totalPoints, progressPercent, isResetting]);

  // Generate sparkle particles
  const generateSparkles = () => {
    return Array.from({
      length: 12
    }, (_, i) => <motion.div key={i} className="absolute" initial={{
      scale: 0,
      x: 0,
      y: 0,
      opacity: 1
    }} animate={{
      scale: [0, 1, 0],
      x: [0, (Math.random() - 0.5) * 200],
      y: [0, (Math.random() - 0.5) * 200],
      opacity: [1, 1, 0]
    }} transition={{
      duration: 1,
      delay: Math.random() * 0.3,
      ease: "easeOut"
    }} style={{
      left: '50%',
      top: '50%'
    }}>
        <Star className="h-4 w-4 text-warning fill-current" />
      </motion.div>);
  };
  const isMaxLevel = level >= MAX_LEVEL;
  const displayLevel = isResetting ? animatedLevel : level;
  const displayPoints = isResetting ? animatedPoints : totalPoints;
  const displayProgress = isResetting ? animatedProgress : progressPercent;

  
  return <motion.div 
    className="bg-card border border-border dark:border-muted-foreground/20 rounded-lg shadow-md h-full flex flex-col p-6 relative overflow-hidden"
    initial={isInitialAppearance ? { 
      opacity: 0, 
      scale: 0.95, 
      y: 20 
    } : false}
    animate={isInitialAppearance ? { 
      opacity: 1, 
      scale: 1, 
      y: 0
    } : (isLevelingUp ? {
      scale: [1, 1.02, 1],
      boxShadow: ["0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)", "var(--shadow-glow)", "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"]
    } : {})}
    transition={isInitialAppearance ? {
      duration: 0.8,
      ease: "easeOut"
    } : {
      duration: 0.6
    }}>
      {/* Sparkle Animation */}
      <AnimatePresence>
        {isLevelingUp && <div className="absolute inset-0 pointer-events-none">
            {generateSparkles()}
          </div>}
      </AnimatePresence>

      {/* Level Up Celebration Banner */}
      <AnimatePresence>
        {isLevelingUp && <motion.div className="absolute inset-0 flex items-center justify-center pointer-events-none z-30" initial={{
        opacity: 0,
        scale: 0.5
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.5
      }} transition={{
        duration: 0.3
      }}>
            <motion.div className={`${isMobile && showMissions ? 'px-3 py-2 text-sm gap-1' : 'px-6 py-3 text-lg gap-2'} rounded-full flex items-center font-bold text-foreground`} style={{
          background: 'linear-gradient(to right, hsl(var(--level-up-gradient-start)), hsl(var(--level-up-gradient-end)))',
          boxShadow: 'var(--shadow-level-up)'
        }} animate={{
          y: [0, -10, 0],
          rotate: [0, 2, -2, 0]
        }} transition={{
          duration: 0.4,
          repeat: 1,
          ease: "easeInOut"
        }}>
              <span>LEVEL UP!</span>
            </motion.div>
          </motion.div>}
      </AnimatePresence>


      {/* Background glow effect */}
      <AnimatePresence>
        {isLevelingUp && <motion.div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-accent/20 rounded-lg" initial={{
        opacity: 0
      }} animate={{
        opacity: [0, 0.8, 0]
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 1,
        repeat: 0
      }} />}
      </AnimatePresence>

      {/* UI Content with animations */}
      <div className="relative z-20">
        <div className="flex items-center justify-between mb-4">
          <motion.div
            initial={isInitialAppearance ? { opacity: 0, x: -20 } : false}
            animate={isInitialAppearance ? { opacity: 1, x: 0 } : {}}
            transition={isInitialAppearance ? { duration: 0.6, delay: 0.3, ease: "easeOut" } : {}}
          >
            <motion.h2 className="font-bold text-foreground text-xl" animate={isLevelingUp ? {
            scale: [1, 1.1, 1],
            color: ["hsl(var(--foreground))", "hsl(var(--primary))", "hsl(var(--foreground))"]
          } : isResetting ? {
            color: ["hsl(var(--foreground))", "hsl(var(--destructive))", "hsl(var(--foreground))"]
          } : {}} transition={{
            duration: 1
          }}>
              Level {Math.floor(displayLevel)}
            </motion.h2>
            <motion.p animate={isLevelingUp ? {
            y: [0, -3, 0]
          } : isResetting ? {
            color: ["hsl(var(--muted-foreground))", "hsl(var(--destructive))", "hsl(var(--muted-foreground))"]
          } : {}} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="text-muted-foreground text-sm">
              Total Points: {Math.floor(displayPoints)}
            </motion.p>
          </motion.div>
          <motion.div 
            className="text-right"
            initial={isInitialAppearance ? { opacity: 0, x: -20 } : false}
            animate={isInitialAppearance ? { opacity: 1, x: 0 } : {}}
            transition={isInitialAppearance ? { duration: 0.6, delay: 0.4, ease: "easeOut" } : {}}
          >
            {isMaxLevel ? null : (
              <>
                {!(isMobile && showMissions) && (
                  <>
                    <div className="text-sm text-muted-foreground">Next level</div>
                    <div className="text-sm font-semibold text-foreground">{currentLevelPoints} / {totalLevelPoints}</div>
                  </>
                )}
              </>
            )}
          </motion.div>
        </div>
        
        <motion.div 
          className="flex justify-between text-xs text-muted-foreground mb-2"
          initial={isInitialAppearance ? { opacity: 0, x: -20 } : false}
          animate={isInitialAppearance ? { opacity: 1, x: 0 } : {}}
          transition={isInitialAppearance ? { duration: 0.6, delay: 0.5, ease: "easeOut" } : {}}
        >
          <span>{isMaxLevel ? "MAX LEVEL!" : `Level ${Math.floor(displayLevel)}`}</span>
          {!isMaxLevel && <span>Level {Math.floor(displayLevel) + 1}</span>}
        </motion.div>
        
        {/* Experience Bar */}
        <motion.div 
          className="w-full bg-accent rounded-full h-2 overflow-hidden origin-left" 
          initial={isInitialAppearance ? { scaleX: 0 } : false}
          animate={isInitialAppearance ? { scaleX: 1 } : (isLevelingUp ? {
            scale: [1, 1.02, 1]
          } : isResetting ? {
            scale: [1, 0.98, 1]
          } : {})}
          transition={isInitialAppearance ? {
            duration: 0.8,
            delay: 0.6,
            ease: "easeOut"
          } : {
            duration: 0.7,
            delay: 0.3
          }}
        >
          <div 
            className={`h-full transition-all duration-300 ease-out ${
              isResetting ? 'bg-destructive' : isMaxLevel ? 'bg-warning' : 'bg-experience'
            }`}
            style={{
              width: `${isMaxLevel ? 100 : Math.min(displayProgress, 100)}%`
            }} 
          />
        </motion.div>
      </div>
    </motion.div>;
}
